import { useId, type ReactElement, cloneElement } from "react";

import { cn } from "../cn";
import { Label } from "./input";

export interface FieldProps {
  label: string;
  /** Validation message; marks the control invalid and links it via aria-describedby. */
  error?: string | undefined;
  hint?: string | undefined;
  className?: string;
  children: ReactElement<{ id?: string; "aria-invalid"?: boolean; "aria-describedby"?: string }>;
}

/** Label + control + hint/error with correct accessibility wiring. */
export function Field({ label, error, hint, className, children }: FieldProps) {
  const id = useId();
  const hintId = `${id}-hint`;
  const errorId = `${id}-error`;
  const describedBy = [hint !== undefined ? hintId : null, error !== undefined ? errorId : null]
    .filter(Boolean)
    .join(" ");
  return (
    <div className={cn("grid gap-1.5", className)}>
      <Label htmlFor={id}>{label}</Label>
      {cloneElement(children, {
        id,
        "aria-invalid": error !== undefined ? true : undefined,
        ...(describedBy ? { "aria-describedby": describedBy } : {}),
      })}
      {hint !== undefined && error === undefined ? (
        <p id={hintId} className="text-xs text-muted-foreground">{hint}</p>
      ) : null}
      {error !== undefined ? (
        <p id={errorId} role="alert" className="text-xs font-medium text-destructive">{error}</p>
      ) : null}
    </div>
  );
}
